/**
 * HTTP client for v06 unified events (m2b-tools europe-west1).
 * Public list/detail can opt in via REACT_APP_USE_UNIFIED_EVENTS_API.
 */

import { getEventCalendarDay } from '../dateUtils';
import {
  isHomepageFeaturedEditorialPick,
  sortFeaturedEventsByDate,
} from '../eventEditorialPick';
import { cloudFunctionsOrigin } from '../shared/cloudFunctions';

const MAPTODO_CF_ORIGIN = cloudFunctionsOrigin();

function eventsApiOrigin() {
  const configured =
    typeof process.env.REACT_APP_EVENTS_API_URL === 'string'
      ? process.env.REACT_APP_EVENTS_API_URL.trim()
      : '';
  return configured || MAPTODO_CF_ORIGIN;
}

export function isUnifiedEventsApiEnabled() {
  const raw = process.env.REACT_APP_USE_UNIFIED_EVENTS_API;
  if (raw == null || raw === '') return false;
  return raw === '1' || raw === 'true' || raw === 'TRUE';
}

const MONTH_NAMES = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Canonical mobile/API startDate (DD/MM/YYYY) → Tools dateStr (DD/MMM/YY).
 * @param {string|null|undefined} startDate
 * @returns {string}
 */
export function startDateToDateStr(startDate) {
  if (!startDate || typeof startDate !== 'string') return '';
  const parts = startDate.split('/');
  if (parts.length !== 3) return '';
  const day = String(parseInt(parts[0], 10)).padStart(2, '0');
  const monthIndex = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11 || Number.isNaN(year)) {
    return '';
  }
  const yy = String(year).slice(-2);
  return `${day}/${MONTH_NAMES[monthIndex]}/${yy}`;
}

/**
 * Map v06 UnifiedEvent → existing public card/detail Firestore-like shape.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {Record<string, unknown>|null}
 */
export function apiEventToPublicCard(event) {
  if (!event || typeof event !== 'object') return null;
  const imageUri =
    (typeof event.cardImgUri === 'string' && event.cardImgUri) ||
    (Array.isArray(event.images) && event.images[0] && event.images[0].uri) ||
    '';
  const dateStr =
    (typeof event.dateDisplay === 'string' && event.dateDisplay) ||
    startDateToDateStr(event.startDate);

  return {
    ...event,
    id: event.id,
    title: event.title || '',
    description: event.description || '',
    dateStr,
    location: event.locationName || event.location || '',
    locationId: event.locationId,
    categories: Array.isArray(event.categories) ? event.categories : [],
    startTime: event.startTime || null,
    endTime: event.endTime || null,
    imageUrl: imageUri,
    cardImageUrl: imageUri,
    editorialPick: event.editorialPick,
    posterLink: event.posterLink,
    link: event.link,
    instagramUrl: event.instagramUrl,
    instagramEvent: event.instagramEvent,
    artistIds: event.artistIds,
    artistLineup: event.artistLineup,
    doorTime: event.doorTime,
    price: event.price,
    organizer: event.organizer,
    ageNote: event.ageNote,
    audioUrl: event.audioUrl,
    audioTitle: event.audioTitle,
  };
}

const CACHE_TTL_MS = 3 * 60 * 1000;
let listCache = null;

function readListCache(key) {
  if (!listCache || listCache.key !== key) return null;
  if (Date.now() - listCache.at >= CACHE_TTL_MS) return null;
  return listCache.value;
}

function writeListCache(key, value) {
  listCache = { key, at: Date.now(), value };
}

/**
 * @param {{ limit?: number, cursor?: string|null, scope?: string }} [opts]
 * @returns {Promise<{ events: Array<Record<string, unknown>>, posters: Array<Record<string, unknown>>, nextCursor: string|null, hasMore: boolean }>}
 */
export async function fetchUnifiedEvents(opts = {}) {
  const params = new URLSearchParams();
  if (opts.scope === 'all') {
    params.set('scope', 'all');
  } else {
    params.set('limit', String(opts.limit || 12));
    if (opts.cursor) params.set('cursor', opts.cursor);
  }
  const cacheKey = params.toString();
  const cached = readListCache(cacheKey);
  if (cached) return cached;

  const url = `${eventsApiOrigin()}/v06GetUnifiedEvents?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Unified events ${res.status}`);
  }
  const payload = await res.json();
  const value = {
    events: Array.isArray(payload.events) ? payload.events.map(apiEventToPublicCard).filter(Boolean) : [],
    posters: Array.isArray(payload.posters) ? payload.posters : [],
    nextCursor: payload.nextCursor || null,
    hasMore: Boolean(payload.hasMore),
  };
  writeListCache(cacheKey, value);
  return value;
}

/**
 * @param {string} id
 * @returns {Promise<Record<string, unknown>|null>}
 */
export async function fetchUnifiedEventById(id) {
  if (!id) return null;
  const url = `${eventsApiOrigin()}/v06GetEventById?id=${encodeURIComponent(id)}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Unified event ${res.status}`);
  }
  const payload = await res.json();
  return apiEventToPublicCard(payload.event);
}

export function invalidateUnifiedEventsCache() {
  listCache = null;
}

function inCalendarWindow(event, start, end) {
  const day = getEventCalendarDay(event);
  return Boolean(day && day >= start && day < end);
}

/**
 * Drain v06 cursor pages until events cover [start, end) or the feed ends.
 * Homepage code must call this instead of paging by 12 cards.
 *
 * @param {{ start: Date, end: Date }} opts
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function fetchUnifiedEventsInRange({ start, end } = {}) {
  if (!(start instanceof Date) || !(end instanceof Date)) return [];

  const collected = [];
  let cursor = null;
  let hasMore = true;

  while (hasMore) {
    const page = await fetchUnifiedEvents({ limit: 50, cursor });
    const incoming = Array.isArray(page.events) ? page.events : [];
    incoming.forEach((event) => collected.push(event));
    cursor = page.nextCursor || null;
    hasMore = Boolean(page.hasMore);
    const last = incoming[incoming.length - 1];
    const lastDay = getEventCalendarDay(last);
    if (lastDay && lastDay >= end) break;
    if (!hasMore) break;
  }

  return collected
    .filter((event) => inCalendarWindow(event, start, end))
    .sort((a, b) => {
      const dayA = getEventCalendarDay(a);
      const dayB = getEventCalendarDay(b);
      return dayA.getTime() - dayB.getTime();
    });
}

/**
 * Drain v06 pages until enough Save the date cards exist or the feed ends.
 *
 * @param {{ pageSize?: number }} [opts]
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function fetchUnifiedFeaturedEvents({ pageSize = 40 } = {}) {
  const collected = [];
  let cursor = null;
  let hasMore = true;

  while (hasMore) {
    const page = await fetchUnifiedEvents({ limit: 50, cursor });
    const incoming = Array.isArray(page.events) ? page.events : [];
    incoming.forEach((event) => collected.push(event));
    cursor = page.nextCursor || null;
    hasMore = Boolean(page.hasMore);
    const featured = collected.filter((event) =>
      isHomepageFeaturedEditorialPick(event.editorialPick, event)
    );
    if (featured.length >= pageSize || !hasMore) {
      return sortFeaturedEventsByDate(featured).slice(0, pageSize);
    }
  }

  return [];
}
